import { useCallback, useEffect, useState } from 'react';
import {
  App, Button, Drawer, Form, Input, Modal, Popconfirm, Space, Table, Tag, Transfer,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  AdminPermissionVo, AdminRoleVo, assignRolePermissions, createRole, deleteRole,
  listPermissions, listRoles, updateRole,
} from '@apis/admin';

interface RoleForm {
  code: string;
  name: string;
  description?: string;
}

export default function RoleTab() {
  const { message } = App.useApp();
  const [data, setData] = useState<AdminRoleVo[]>([]);
  const [perms, setPerms] = useState<AdminPermissionVo[]>([]);
  const [loading, setLoading] = useState(false);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminRoleVo | null>(null);
  const [authRole, setAuthRole] = useState<AdminRoleVo | null>(null);
  const [authTargetKeys, setAuthTargetKeys] = useState<string[]>([]);

  const [createForm] = Form.useForm<RoleForm>();
  const [editForm] = Form.useForm<RoleForm>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([listRoles(), listPermissions()]);
      setData(r);
      setPerms(p);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async () => {
    const values = await createForm.validateFields();
    await createRole(values);
    message.success('已创建');
    setCreating(false);
    createForm.resetFields();
    load();
  };

  const submitEdit = async () => {
    if (!editing) return;
    const values = await editForm.validateFields();
    await updateRole(editing.id, { name: values.name, description: values.description });
    message.success('已更新');
    setEditing(null);
    load();
  };

  const submitDelete = async (r: AdminRoleVo) => {
    if (r.builtin) {
      message.warning('内置角色不可删除');
      return;
    }
    await deleteRole(r.id);
    message.success('已删除（持有该角色的用户 access_token 在 60s 内失效）');
    load();
  };

  const submitAuth = async () => {
    if (!authRole) return;
    await assignRolePermissions(authRole.id, authTargetKeys);
    message.success('授权成功（持有该角色的用户 access_token 在 60s 内失效）');
    setAuthRole(null);
    load();
  };

  const columns: ColumnsType<AdminRoleVo> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '角色',
      key: 'role',
      render: (_, r) => (
        <Space>
          <span>{r.name}</span>
          <Tag color="blue">{r.code}</Tag>
          {r.builtin && <Tag color="orange">内置</Tag>}
        </Space>
      ),
    },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    {
      title: '权限点',
      dataIndex: 'permissions',
      render: (codes: string[]) => (
        codes.length === 0
          ? <Tag>无</Tag>
          : <span style={{ color: '#888' }}>{codes.length} 项</span>
      ),
    },
    {
      title: '操作',
      key: 'op',
      width: 280,
      render: (_, r) => (
        <Space wrap size={4}>
          <Button
            size="small"
            icon={<SafetyOutlined />}
            onClick={() => {
              setAuthRole(r);
              setAuthTargetKeys(r.permissions);
            }}
          >
            授权限
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(r);
              editForm.setFieldsValue({
                code: r.code,
                name: r.name,
                description: r.description ?? undefined,
              });
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title={`确认删除角色 ${r.name}?`}
            onConfirm={() => submitDelete(r)}
            okButtonProps={{ danger: true }}
            disabled={r.builtin}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={r.builtin} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={() => load()}>刷新</Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>新增角色</Button>
      </Space>

      <Table<AdminRoleVo>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
      />

      <Modal
        title="新增角色"
        open={creating}
        onCancel={() => { setCreating(false); createForm.resetFields(); }}
        onOk={submitCreate}
        okText="创建"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item label="角色 code" name="code" rules={[{ required: true, pattern: /^[a-zA-Z0-9_:-]+$/ }]}>
            <Input placeholder="如 ops / dev_lead" />
          </Form.Item>
          <Form.Item label="名称" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editing ? `编辑角色 ${editing.code}` : ''}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={submitEdit}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="名称" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={authRole ? `给 ${authRole.name} 授权限点` : ''}
        open={!!authRole}
        onClose={() => setAuthRole(null)}
        width={720}
        extra={<Button type="primary" onClick={submitAuth}>保存</Button>}
      >
        <Transfer
          dataSource={perms.map((p) => ({
            key: p.code,
            title: `${p.name} (${p.code})`,
            description: p.description ?? '',
          }))}
          targetKeys={authTargetKeys}
          onChange={(keys) => setAuthTargetKeys(keys as string[])}
          render={(item) => item.title ?? ''}
          showSearch
          listStyle={{ width: 320, height: 480 }}
          titles={['可选', '已授权']}
        />
      </Drawer>
    </div>
  );
}
